import React, { Fragment, useEffect, useState } from "react"
import {
  Box,
  Button,
  Grid,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material"

import Form from "react-bootstrap/Form"
import Dropdown from "react-bootstrap/Dropdown"
import { FaChevronLeft } from "react-icons/fa6"
import * as Style from "./style"
import { BiSolidOffer } from "react-icons/bi"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { useAppSelector } from "../../app/hooks"
import { FaChevronDown } from "react-icons/fa6"
import {
  useCreatepaymentMutation,
  usePaymentBookingMutation,
} from "../../features/AuthPayment/paymentApi"
import { useAppDispatch } from "../../app/store"
import { toast } from "react-toastify"
import {
  useCreatePostApiMutation,
  useDiscountOffersQuery,
  useUpdatePostTimeMutation,
  useConfirmMultipleBookingsMutation,
  useGetAcceptedGeneratedPostsAllQuery,
} from "../../features/OverViewAuth/overViewApi"
import { checkBookingConflict } from "../../utils/bookingValidation"
import { setBookingDetails } from "../../features/PostCreat/postcreateSlice"
import ConfirmPost from "../common/commonModal/commonModal"
import money from "../../images/money.png"

const steps = ["Booking Summary", "Payment", "Request Sent"]

const BookOverview = () => {
  const [activeStep, setActiveStep] = React.useState(1)
  const [openModal, setOpenModal] = useState(false)
  const [isLoadings, setIsLoadings] = useState(false)
  const [skipped, setSkipped] = React.useState(new Set<number>())
  // stripe session/payment API
  const [paymentPost, { isLoading: paymentIsLoading }] =
    usePaymentBookingMutation()
  // backend tracking API
  const [createPayment, { data: createPaymentData, isLoading: createPaymentLoading }] =
    useCreatepaymentMutation()

  const [selectedOffer, setSelectedOffer] = useState<number>(0)
  const [offerBooks, setOffersBooks] = useState(false)
  const [searchparams] = useSearchParams()
  const postId = searchparams.get("postId")

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [showPop, setShowPop] = useState(false)
  const handleClose = () => {
    setShowPop(false)
  }

  const bookingDetails = useAppSelector(
    (state: any) => state.postCreate?.bookingDetails,
  )
  const user = useAppSelector((state: any) => state.auth.user)

  const { data: discountOffers } = useDiscountOffersQuery()
  const [updateTime, { isLoading: isLoadingUpdate }] =
    useUpdatePostTimeMutation()
  const [
    postCreate,
    {
      isError: bookingisError,
      error: bookingError,
      data: bookingData,
      isLoading: isloadingBooking,
    },
  ] = useCreatePostApiMutation()

  const { data: existingBookingsData } = useGetAcceptedGeneratedPostsAllQuery({});

  const isStepOptional = (step: number) => {
    return step === 1
  }

  const isStepSkipped = (step: number) => {
    return skipped.has(step)
  }

  const location = useLocation()
  const [confirmMultipleBookings] = useConfirmMultipleBookingsMutation()

  const paymentCalculations = location.state?.paymentCalculations

  const bookingIds = location.state?.bookingIds || []
  const bookingCount = bookingIds.length > 0 ? bookingIds.length : 1

  // Base Calculation
  const manualTotalPayment =
    (Number(bookingDetails?.postPrice) || 0) *
    (bookingDetails?.hours || 0) *
    (bookingDetails?.personRequired || 0) *
    bookingCount;

  // Match backend "subtotal" from provided JSON ({ subtotal: 150, ... })
  // Prioritize Backend Values from paymentCalculations
  const backendSubtotal = paymentCalculations?.subtotal !== undefined ? Number(paymentCalculations.subtotal) : undefined;
  const backendTaxAmount = paymentCalculations?.taxAmount !== undefined ? Number(paymentCalculations.taxAmount) : undefined;
  const backendTotalAmount = paymentCalculations?.totalAmount !== undefined ? Number(paymentCalculations.totalAmount) : undefined;

  // 1. Calculate Base Amount (Subtotal)
  // If backend provided subtotal, use it. Otherwise fall back to manual calc (frontend) or total-tax derivation.
  const totalPayment = backendSubtotal !== undefined
    ? backendSubtotal
    : (backendTotalAmount !== undefined && backendTaxAmount !== undefined
      ? backendTotalAmount - backendTaxAmount
      : manualTotalPayment);

  const isOfferApplied = selectedOffer > 0;

  // Discount Calculation
  const discountPercentage = isOfferApplied ? selectedOffer : 0;
  const calculatedDiscount = (discountPercentage / 100) * totalPayment;
  const discountAmount = Math.min(calculatedDiscount, totalPayment);
  const beforeTax = Math.max(0, totalPayment - discountAmount);

  let taxAmount = 0;
  if (isOfferApplied && paymentCalculations?.taxPercent !== undefined) {
    // Recalculate tax: (DiscountedSubtotal * TaxPercent / 100)
    taxAmount = (beforeTax * Number(paymentCalculations.taxPercent)) / 100;
  } else if (backendTaxAmount !== undefined) {
    // Exact Backend Tax
    taxAmount = backendTaxAmount;
  } else {
    // Fallback Frontend Calculation
    const taxRate = Number(bookingDetails?.tax) || 0;
    taxAmount = (beforeTax * taxRate) / 100;
  }

  const displayTaxPercent = paymentCalculations?.taxPercent
    ? Number(paymentCalculations.taxPercent)
    : (Number(bookingDetails?.tax) || 0);

  const totalPrice = (!isOfferApplied && backendTotalAmount !== undefined)
    ? backendTotalAmount
    : Number((beforeTax + taxAmount).toFixed(2));

  // Display Variables
  const breakdown = paymentCalculations?.breakdown;
  const displayHours = paymentCalculations?.totalHours !== undefined
    ? Number(paymentCalculations.totalHours)
    : (breakdown
      // sum up all hours from breakdown if available
      ? breakdown.reduce((acc: number, item: any) => acc + (item.hours || 0), 0)
      : (bookingDetails?.hours || 0) * bookingCount);

  const displayRate = paymentCalculations?.breakdown?.[0]?.pricePerHour
    ?? paymentCalculations?.breakdown?.[0]?.specialPricePerHour
    ?? bookingDetails?.postPrice;

  const processPayments = async (backendPayload: any, stripePayload: any) => {
    const backendRes: any = await createPayment(backendPayload).unwrap().catch((err: any) => {
      throw new Error(err?.data?.message || err?.message || "Backend payment creation failed")
    })

    const backendPaymentId = backendRes?.data?.paymentId || backendRes?.data?._id || backendRes?.data?.id || null

    if (backendPaymentId) {
      stripePayload = { ...stripePayload, backendPaymentId }
    }
    const stripeRes: any = await paymentPost(stripePayload).unwrap().catch((err: any) => {
      throw new Error(err?.data?.message || err?.message || "Stripe payment creation failed")
    })

    const url = stripeRes?.data?.url || stripeRes?.url || stripeRes?.checkoutUrl
    if (!url) {
      throw new Error("Payment URL not found from Stripe response")
    }

    // redirect to Stripe checkout
    window.location.href = url
  }

  const handleNext = async () => {
    setIsLoadings(true)

    // ------------- MULTIPLE BOOKINGS -------------
    if (bookingIds.length > 0) {
      try {
        const payload = {
          bookingIds: bookingIds,
          customerId: user?._id || user?.id,
          offerAmount: selectedOffer,
          totalAmount: totalPrice,
          tax: taxAmount,
        }

        const response: any = await confirmMultipleBookings(payload)

        if (response?.data && (response.data.status === 200 || !response.data.status)) {
          // prepare payloads
          const backendPayload = {
            // adjust fields based on your backend contract
            bookingIds,
            customerId: user?._id || user?.id,
            tax: taxAmount,
            totalAmount: totalPrice,
            offerAmount: selectedOffer,
            purpose: "multiple_bookings",
          }

          const stripePayload = {
            bookingIds,
            customerId: user?._id || user?.id,
            tax: taxAmount,
            stripepricekey: totalPrice,
            offerAmount: selectedOffer,
            purpose: "multiple_bookings",
          }

          try {
            // Clear calendar bookings from session storage before payment
            sessionStorage.removeItem('booking_calendar_items');
            sessionStorage.removeItem('booking_source_id');
            await processPayments(backendPayload, stripePayload)
            // after redirection we won't reach here; if stripe returns synchronously, you may handle success here
          } catch (err: any) {
            toast.error(err?.message || "An error occurred during payment processing")
          }
        } else {
          toast.error(
            response?.error?.data?.message || "Failed to confirm bookings",
          )
        }
      } catch (error: any) {
        toast.error(error.message || "An error occurred")
      } finally {
        setIsLoadings(false)
      }
      return
    }

    // ------------- SINGLE BOOKING -------------
    const payload = new FormData()
    payload.append("location", bookingDetails?.location)
    payload.append("service", bookingDetails?.service as any)
    payload.append("serviceName", bookingDetails?.serviceName as any)
    payload.append("ageRange", bookingDetails?.ageRange?.toString())
    payload.append("from", bookingDetails?.from)
    payload.append("till", bookingDetails?.till)
    payload.append("description", bookingDetails?.description)
    payload.append(
      "personRequired",
      bookingDetails?.personRequired?.toString(),
    )
    payload.append("language", bookingDetails?.language as any)
    payload.append(
      "extraDetails",
      JSON.stringify(bookingDetails?.extraDetails || {}),
    )
    const fromISO = bookingDetails?.from;

    // Get local bookings from session storage
    const saved = sessionStorage.getItem('booking_calendar_items');
    const localBookings = saved ? JSON.parse(saved) : [];

    // Merge backend and local bookings
    const allBookings = [
      ...(existingBookingsData?.data || []),
      ...localBookings
    ];

    const hasConflict = checkBookingConflict(
      {
        serviceId: bookingDetails?.service,
        from: fromISO,
      },
      allBookings
    );

    if (hasConflict) {
      toast.error("Slot already booked.");
      setIsLoadings(false);
      return;
    }

    bookingDetails?.attachment?.forEach((image: any, index: number) => {
      const key = index === 0 ? "attachment" : `attachment${index}`
      payload.append(key, image as any)
    })

    try {
      const response: any = await postCreate(payload)

      if (response?.data?.status === 200) {
        const createdPostId = response?.data?.data?.createdPost?._id

        // prepare backend tracking payload & stripe payload
        const backendPayload = {
          postId: createdPostId,
          customerId: user?._id || user?.id,
          tax: taxAmount,
          totalAmount: totalPrice,
          offerAmount: selectedOffer,
          purpose: "single_booking",
        }

        const stripePayload = {
          postId: createdPostId,
          customerId: user?._id || user?.id,
          tax: taxAmount,
          stripepricekey: totalPrice,
          offerAmount: selectedOffer,
        }

        try {
          // Clear calendar bookings from session storage before payment
          sessionStorage.removeItem('booking_calendar_items');
          sessionStorage.removeItem('booking_source_id');
          await processPayments(backendPayload, stripePayload)
        } catch (err: any) {
          toast.error(err?.message || "An error occurred during payment processing")
        }
      } else {
        if (response?.error?.status === 400) {
          toast.error(response?.error?.data?.message)
        } else {
          toast.error("Unexpected response from server")
        }
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred")
    } finally {
      setIsLoadings(false)
    }
  }

  // for update time api call function
  const handleupdateTimeApi = async () => {
    setIsLoadings(true)
    const updateTimePayload = {
      from: bookingDetails?.from,
      till: bookingDetails?.till,
      postId: bookingDetails?.postId,
      offerAmount: selectedOffer,
      totalAmount: totalPrice,
      tax: taxAmount,
    }
    try {
      const res: any = await updateTime(updateTimePayload)

      if (res?.data?.status == 200) {
        handleClose()
        navigate("/dashboard/booking-completed")
        dispatch(setBookingDetails(null as any))
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred")
    } finally {
      setIsLoadings(false)
    }
  }

  const handleReset = () => {
    setActiveStep(1)
  }

  useEffect(() => {
    if (bookingDetails?.personRequired == "") {
      setOpenModal(true)
      navigate("/dashboard")
    }
  }, [bookingDetails?.personRequired])

  const handleCheckboxChange = (ammount: number) => {
    setSelectedOffer(ammount)
  }

  const handleBookingsDeatils = () => {
    navigate(-1)
  }

  useEffect(() => {
    const isRefreshed = sessionStorage.getItem("isRefreshed")
    if (isRefreshed) {
      setOpenModal(true)
      sessionStorage.removeItem("isRefreshed")
    } else {
      sessionStorage.setItem("isRefreshed", "true")
    }
  }, [])

  return (
    <>
      <Box>
        <Style.Overview>
          <Box className="feature-card px-1 py-md-4 px-md-5 position-relative">
            <Box>
              <Style.Overview>
                <Box className=" px-1 py-md-4 px-md-5 position-relative">
                  <Button
                    className="back-btn mb-0 step-back-btn"
                    onClick={handleBookingsDeatils}
                  >
                    <FaChevronLeft />
                  </Button>
                  {bookingDetails?.personRequired !== "" && (
                    <Stepper
                      activeStep={activeStep}
                      className="stepper-booking pt-3"
                    >
                      {steps.map((label, index) => (
                        <Step key={label} completed={isStepSkipped(index)}>
                          <StepLabel optional={isStepOptional(2) ? "" : null}>
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}
                  {activeStep === steps.length ? (
                    <React.Fragment>
                      <Typography sx={{ mt: 2, mb: 1 }}>
                        All steps completed - you&apos;re finished
                      </Typography>
                      <Box
                        sx={{ display: "flex", flexDirection: "row", pt: 2 }}
                      >
                        <Box sx={{ flex: "1 1 auto" }} />
                        <Button onClick={handleReset}>Reset</Button>
                      </Box>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Box className="mt-5"></Box>
                      <Box className="mt-5">
                        <Grid container justifyContent="center">
                          {bookingDetails && (
                            <Grid item lg={6} md={5} hidden>
                              {/* <Box style={{ marginRight: "20px" }}>
                                <Dropdown
                                  onClick={() => setOffersBooks(!offerBooks)}
                                >
                                  <Dropdown.Toggle
                                    variant="success"
                                    className="apply-btn"
                                    id="dropdown-basic"
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span>
                                      <BiSolidOffer /> Apply Offer{" "}
                                    </span>
                                    <FaChevronDown
                                      style={{
                                        color: "#f24f98",
                                        paddingLeft: "5px",
                                      }}
                                    />
                                  </Dropdown.Toggle>
                                </Dropdown>
                                {offerBooks && (
                                  <div className="discount-dropdown">
                                    {discountOffers?.data?.length > 0 ? (
                                      discountOffers?.data?.map(
                                        (
                                          offersPrice: any,
                                          index: number,
                                        ) => (
                                          <Fragment key={index}>
                                            <div className="offer offer d-flex justify-content-between">
                                              <div className="offer-text">
                                                <h5>
                                                  Get {offersPrice?.offerAmount}{" "}
                                                  % Offer
                                                </h5>
                                                <p>{offersPrice?.name}</p>
                                              </div>
                                              <div className="offer-button">
                                                <Form>
                                                  <div className="mb-3">
                                                    <input
                                                      style={{
                                                        border:
                                                          "2px solid #cacaca",
                                                        fontSize: "20px",
                                                      }}
                                                      className="form-check-input"
                                                      type="checkbox"
                                                      checked={
                                                        offersPrice?.offerAmount ==
                                                          selectedOffer
                                                          ? true
                                                          : false
                                                      }
                                                      onChange={() =>
                                                        handleCheckboxChange(
                                                          offersPrice?.offerAmount,
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </Form>
                                              </div>
                                            </div>
                                          </Fragment>
                                        ),
                                      )
                                    ) : (
                                      <div
                                        className="no-offer-message"
                                        style={{ color: "#3A3A3A" }}
                                      >
                                        <p>
                                          Currently There Are No Offers On Your
                                          Account.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Box> */}
                            </Grid>
                          )}
                          <Grid item lg={8} md={10} xs={12}>
                            <Box className="total-table" sx={{ mx: 'auto' }}>
                              <Grid container>
                                <Grid item xs={10}>
                                  <Typography className="total-text">
                                    Total Number Of Service Provider{" "}
                                    {bookingCount > 1 &&
                                      `(${bookingCount} Bookings)`}
                                  </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                  <Typography
                                    className="total-text"
                                    style={{ fontWeight: "400" }}
                                  >
                                    {bookingDetails?.personRequired}
                                  </Typography>
                                </Grid>
                                <Grid item xs={10}>
                                  <Typography className="total-text">
                                    Total Number Of Service Hours
                                  </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                  <Typography
                                    className="total-text"
                                    style={{ fontWeight: "400" }}
                                  >
                                    {displayHours}
                                  </Typography>
                                </Grid>

                                {breakdown && breakdown.length > 0 ? (
                                  <>
                                    {breakdown
                                      .filter((item: any) => !item.serviceName?.includes('(With Supplies)'))
                                      .map((item: any, index: number) => (
                                      <React.Fragment key={index}>
                                        <Grid item xs={8} sm={10}>
                                          <Typography className="total-text">
                                            {item.serviceName}
                                            <Typography component="span" sx={{ fontSize: '13px', color: '#666', display: 'block', lineHeight: 1.4 }}>
                                              {item.normalHours > 0 && (
                                                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                                  Normal Price: {item.normalHours} hrs x C${item.pricePerHour}/hr
                                                </Box>
                                              )}
                                              {item.specialHours > 0 && (
                                                <Box component="span" sx={{ display: 'block', color: '#f07fa9', fontWeight: 600 }}>
                                                  Updated Price (Extras Included): {item.specialHours} hrs x C${item.specialPricePerHour}/hr
                                                </Box>
                                              )}
                                              {!(item.normalHours > 0 || item.specialHours > 0) && (
                                                <Box component="span" sx={{ display: 'block' }}>
                                                  {item.hours} hrs - {item.billableHours} x C${item.pricePerHour || item.specialPricePerHour}/hr
                                                </Box>
                                              )}
                                            </Typography>
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                          <Typography
                                            className="total-text"
                                            style={{
                                              fontWeight: "400",
                                              textAlign: "right",
                                              whiteSpace: "nowrap"
                                            }}
                                          >
                                            C$ - {Number(item.amount).toFixed(2)}
                                          </Typography>
                                        </Grid>
                                      </React.Fragment>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    <Grid item xs={8} sm={10}>
                                      <Typography className="total-text">
                                        Per Person Hourly Rate
                                      </Typography>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={4}
                                      sm={2}
                                    >
                                      <Typography
                                        className="total-text"
                                        style={{
                                          fontWeight: "400",
                                          textAlign: "right",
                                          whiteSpace: "nowrap"
                                        }}
                                      >
                                        C$ - {displayRate}
                                      </Typography>
                                    </Grid>
                                  </>
                                )}

                                <Grid item xs={8} sm={10}>
                                  <Typography className="total-text">
                                    Tax Charges {displayTaxPercent > 0 && `(${displayTaxPercent}%) `}
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: "0.85rem",
                                        color: "#666",
                                        pt: "2px",
                                      }}
                                    >
                                      (GST + PST)
                                    </Typography>
                                  </Typography>
                                </Grid>

                                <Grid item xs={4} sm={2}>
                                  <Typography
                                    className="total-text"
                                    style={{
                                      fontWeight: "400",
                                      textAlign: "right",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    C$ - {Number(taxAmount).toFixed(2)}
                                  </Typography>
                                </Grid>

                                <Grid item xs={8} sm={10}>
                                  <Typography className="total-text">
                                    Discount
                                  </Typography>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                  <Typography
                                    className="total-text"
                                    style={{
                                      fontWeight: "400",
                                      textAlign: "right",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    C$ - {(discountAmount || 0).toFixed(2)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={8} sm={10} className="total-col">
                                  <Typography
                                    className="total-text"
                                    style={{
                                      color: "#24AAE3",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Total Payment
                                  </Typography>
                                </Grid>
                                <Grid item xs={4} sm={2} className="total-col">
                                  <Typography
                                    className="total-text"
                                    style={{
                                      color: "#24AAE3",
                                      fontWeight: "600",
                                      textAlign: "right",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    C${(totalPrice || 0).toFixed(2)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {bookingDetails?.postId !== postId ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            pt: 2,
                            justifyContent: "center"
                          }}
                        >
                          {activeStep !== steps.length - 1 && (
                            <Button
                              className="view-profile mb-3"
                              sx={{
                                padding: "10px 30px",
                                animation: 'pulseFinal 2s infinite',
                                '@keyframes pulseFinal': {
                                  '0%': { transform: 'scale(1)', boxShadow: '0 4px 12px rgba(244, 117, 164, 0.2)' },
                                  '50%': { transform: 'scale(1.05)', boxShadow: '0 8px 20px rgba(244, 117, 164, 0.4)' },
                                  '100%': { transform: 'scale(1)', boxShadow: '0 4px 12px rgba(244, 117, 164, 0.2)' },
                                }
                              }}
                              onClick={handleNext}
                            >
                              {activeStep === steps.length - 2
                                ? "Submit"
                                : "Payment"}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            pt: 2,
                            justifyContent: "center"
                          }}
                        >
                          {activeStep !== steps.length - 1 && (
                            <Button
                              className="view-profile"
                              style={{ padding: "10px 30px" }}
                              onClick={() => setShowPop(true)}
                            >
                              Update
                            </Button>
                          )}
                        </Box>
                      )}
                    </React.Fragment>
                  )}

                </Box>
              </Style.Overview>
            </Box>
          </Box>
        </Style.Overview>
      </Box>
    </>
  )
}

export default BookOverview